import React, { useEffect, useRef } from 'react';
import { X, ExternalLink, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackImpression, trackClick } from '../../services/promotionTracking';
import API_BASE_URL from '../../config/api';

/**
 * Composant pour afficher une promotion flottante à gauche
 */
const PromotionFloating = ({ promotion, onClose, onOrder }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasTrackedImpression = useRef(false);

  // Tracker l'impression quand le composant est monté (uniquement pour les promotions réelles)
  useEffect(() => {
    if (promotion?.id && !hasTrackedImpression.current) {
      // Ne tracker que si l'ID est numérique (pas une promotion de démo)
      const promotionId = typeof promotion.id === 'number' ? promotion.id : parseInt(promotion.id);
      if (!isNaN(promotionId) && promotionId > 0) {
        trackImpression(promotionId, 'floating_left');
        hasTrackedImpression.current = true;
      }
    }
  }, [promotion?.id]);

  const handleCTA = () => {
    // Tracker le clic (uniquement pour les promotions réelles)
    if (promotion?.id) {
      const promotionId = typeof promotion.id === 'number' ? promotion.id : parseInt(promotion.id);
      if (!isNaN(promotionId) && promotionId > 0) {
        trackClick(promotionId, 'floating_left', {
          cta_type: promotion.cta_type,
          action: promotion.cta_type === 'order' ? 'order_click' : 'discover_click',
        });
      }
    }

    if (promotion.cta_type === 'order' && onOrder) {
      onOrder(promotion);
      return;
    }

    const ctaUrl = (promotion?.cta_url || '').trim();
    if (ctaUrl) {
      if (/^https?:\/\//i.test(ctaUrl)) {
        window.open(ctaUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(ctaUrl.startsWith('/') ? ctaUrl : `/${ctaUrl}`);
      return;
    }

    const dishId = Array.isArray(promotion?.dish_ids) && promotion.dish_ids.length > 0
      ? Number(promotion.dish_ids[0]) || null
      : null;
    if (dishId) {
      navigate(`/product/${dishId}`);
      return;
    }

    const menuId = Array.isArray(promotion?.menu_ids) && promotion.menu_ids.length > 0
      ? Number(promotion.menu_ids[0]) || null
      : null;
    if (menuId && promotion.restaurant_id) {
      navigate(`/restaurant/${promotion.restaurant_id}?menu=${menuId}`);
      return;
    }

    if (promotion.restaurant_id) {
      navigate(`/restaurant/${promotion.restaurant_id}`);
    } else {
      navigate('/');
    }
  };

  if (!promotion) return null;

  const normalizeMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    return trimmed.startsWith('/') ? `${base}${trimmed}` : `${base}/${trimmed}`;
  };

  const imageUrl = normalizeMediaUrl(promotion.media_url || promotion.image_url);
  const videoUrl = normalizeMediaUrl(promotion.video_url || (promotion.media_type === 'video' ? promotion.media_url : null));
  const shortDesc = promotion.short_description || promotion.description;
  const ctaText = promotion.cta_label || promotion.cta_text;

  return (
    <div
      className="w-full md:w-64 rounded-lg shadow-2xl overflow-hidden animate-slide-in-left relative"
      style={{ backgroundColor: promotion.background_color || '#FFFFFF' }}
    >
      <button
        onClick={onClose}
        className="absolute top-2 right-2 z-10 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70 transition-all"
      >
        <X size={16} />
      </button>
      {promotion.label && (
        <div className="absolute top-2 left-2 z-10">
          <span className={`px-2 py-1 rounded text-xs font-bold ${
            promotion.label === 'offre_du_jour' ? 'bg-yellow-500 text-white' : 'bg-green-500 text-white'
          }`}>
            {promotion.label === 'offre_du_jour' ? t('promotions.dealOfTheDay') : t('promotions.new')}
          </span>
        </div>
      )}
      <div className="relative w-full h-40 overflow-hidden">
        {promotion.media_type === 'video' && videoUrl ? (
          <video src={videoUrl} autoPlay loop muted className="w-full h-full object-cover" />
        ) : (
          <img src={imageUrl || 'https://placehold.co/640x384?text=Promotion'} alt={promotion.title} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold text-lg text-gray-900 mb-2">{promotion.title}</h3>
        {shortDesc && (
          <p className="text-sm text-gray-900 font-normal mb-3 line-clamp-2">{shortDesc}</p>
        )}
        <div className="flex items-center space-x-2 mb-3">
          <span className="text-2xl font-bold text-green-600">
            {promotion.final_price != null ? Number(promotion.final_price).toFixed(2) : '—'}€
          </span>
          {promotion.original_price != null && promotion.final_price != null && promotion.original_price > promotion.final_price && (
            <span className="text-sm text-gray-900 font-normal line-through">{Number(promotion.original_price).toFixed(2)}€</span>
          )}
        </div>
        <button
          onClick={handleCTA}
          className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
        >
          {promotion.cta_type === 'order' ? (
            <><ShoppingCart size={16} /><span>{ctaText || t('promotions.order')}</span></>
          ) : (
            <><ExternalLink size={16} /><span>{ctaText || t('promotions.discover')}</span></>
          )}
        </button>
      </div>
    </div>
  );
};

export default PromotionFloating;
